import { forwardRef } from "react";
import type { SelectHTMLAttributes } from "react";
import { ChevronDown } from "lucide-react";
import { useFieldId } from "./useFieldId";
import { cn } from "@/lib/utils";
import Label from "./Label";
import { FieldError, fieldErrorId } from "./FieldError";
import { FieldHint, fieldHintId } from "./FieldHint";
import { fieldControlClass } from "./field-variants";

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  hint?: string;
  options: SelectOption[];
  placeholder?: string;
}

const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    { className, label, error, hint, options, placeholder, id, name, ...props },
    ref,
  ) => {
    const selectId = useFieldId(id, name);
    const errorId = selectId ? fieldErrorId(selectId) : undefined;
    const hintId = selectId ? fieldHintId(selectId) : undefined;

    return (
      <div className="w-full">
        {label ? (
          <Label htmlFor={selectId} spacing="field">{label}</Label>
        ) : null}
        <div className="relative">
          <select
            ref={ref}
            id={selectId}
            name={name}
            className={fieldControlClass({
              className: cn(
                "cursor-pointer appearance-none pr-10",
                className,
              ),
            })}
            aria-invalid={error ? "true" : "false"}
            aria-describedby={
              error ? errorId : hint ? hintId : undefined
            }
            {...props}
          >
            {placeholder ? (
              <option value="" disabled>
                {placeholder}
              </option>
            ) : null}
            {options.map((option) => (
              <option
                key={option.value}
                value={option.value}
                disabled={option.disabled}
              >
                {option.label}
              </option>
            ))}
          </select>
          <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-surface-400">
            <ChevronDown className="h-4 w-4" />
          </div>
        </div>
        {error && errorId ? (
          <FieldError id={errorId}>{error}</FieldError>
        ) : null}
        {hint && !error && hintId ? (
          <FieldHint id={hintId}>{hint}</FieldHint>
        ) : null}
      </div>
    );
  },
);

Select.displayName = "Select";

export default Select;
